// Third-party
import React from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps, Redirect, generatePath } from 'react-router-dom';

// Components
import { Header, OverlappingBackground } from '../../../components/layout';
import { LoadingSpinner } from '../../../components/common';
import LegacyContactInfoCard from './LegacyContactInfoCard';
import ContactDetailsTabs from './ContactDetailsTabs';
import { Breadcrumb } from '../../../components/navigation';
import { BreadcrumbItem } from '../../../components/navigation/BreadcrumbNav';
import { DeleteContactsModal, DeleteComparisonsModal, CreateKycQuestionnaire, ComparisonMissingItemsModal } from '../../../components/modals';
import { DestructiveModal } from '@capintel/component-lib/destructive-modal';
import { Stack } from '@capintel/component-lib/stack';

// Actions
import {
  getContactById,
  loadContactData,
  saveContact,
  deleteContact,
  getContactPortfolios,
  getContactComparisons,
  deleteContactComparison,
  saveContactRiskProfile,
  deleteContactRiskProfile,
  getContactActivity,
  setPersistenceSource,
  SET_IS_SAVING_CONTACT_RISK_PROFILE,
} from '../../../../redux/modules/contacts';
import {
  createComparison,
  getComparisonById,
  loadComparison,
  rerunComparison,
  attachRiskProfile,
  skipEmptyState,
  setTab as setComparisonTab,
  removeMissingComparisonData,
  closeComparison,
  DELETE_COMPARISON,
} from '../../../../redux/modules/comparison';
import { setBaseDob } from '../../../../redux/modules/bmo-gif';
import { resetCurrentContact, resetGif } from '../../../../redux/modules/common/actionCreators';
import {
  getKycQuestionnaires,
  loadKycQuestionnaire,
  resetRiskProfile,
  setRiskProfileForEdit,
  SET_IS_RESETTING_RISK_PROFILE,
} from '../../../../redux/modules/kyc-questionnaire';
import {
  selectIsLoadingEvery,
  selectIsLoadingParamsEvery,
  startLoaderAction,
  stopLoaderAction,
  selectIsLoadingSome,
} from '../../../../redux/modules/ui/loaders';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '../../../../utils/3rd-party/heap';
import { NAV_PATHS, COMPARISON_NAV_PATHS } from '../../../../utils/constants';
import notification from '../../../../utils/notification';
import { formatLocalizedString, formatMessage, FormattedMessage } from '../../../../utils/i18n';
import { showError } from '../../../../utils/errors';
import { getActiveQuestionnaire } from '../../../../utils/risk-profiles';
import { getContactLayout, CONTACT_LAYOUT } from '../../../../utils/contacts';
import { comparisonHasGifItems, comparisonHasMissingItems } from '../../../../utils/comparison';
import { userHasPermissionEnabled, PERMISSIONS, userHasPermission } from '../../../../utils/permissions';
import { CurrentContact } from '../../../../redux/modules/types/contacts';
import { ReduxState } from '../../../../redux/modules/types';
import sessionStorage, { SessionStorageKey } from '../../../../utils/session-storage';
import { SortingField } from '../../../../types/data';
import Dictionary from '@/types/dictionary';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import Comparison from '@/types/comparison';
import Locale from '@/types/locale';
import ContactRiskProfile from '@/types/contact-risk-profile';
import GenericContact from '@/types/generic-contact';
import Portfolio from '@/types/portfolio';
import ComparisonContact from '@/types/comparison-contact';
import RiskProfileItem from '@/types/risk-profile-item';
import Activity from '@/types/activity';
import ReducedComparison from '@/types/reduced-comparison';
import User from '@/types/user';
import AppMode from '@/types/app-mode';
import { PERSISTENCE_KEYS } from '@/utils/persistence';
import localStorageUtils from '@/utils/local-storage';

type MatchParams = {
  contactId: string;
};

type StateProps = {
  currentContact?: CurrentContact;
  questionnaires: KycQuestionnaire[];
  loadedComparison?: Comparison;
  locale: Locale;
  loading: {
    current: {
      portfolios: boolean;
      comparisons: boolean;
      activityLogs: boolean;
      nextContactPortfoliosToken?: string;
      nextContactComparisonsToken?: string;
    };
  };
  newRiskProfile?: Partial<ContactRiskProfile>;
  currentQuestionnaire?: KycQuestionnaire;
  isDeletingContactComparison: boolean;
  openingComparisonIds: string[];
  rerunningComparisonIds: string[];
  isSavingRiskProfile?: boolean;
  persistenceSource?: string;
};

type DispatchProps = {
  getContactById: (id: string) => Promise<GenericContact>;
  loadContactData: (contact: GenericContact) => Promise<void>;
  saveContact: (contact: Partial<GenericContact>) => Promise<GenericContact>;
  deleteContact: (contact: GenericContact) => Promise<void>;
  resetCurrentContact: () => void;
  getContactComparisons: (contact: GenericContact, query?: string) => Promise<Comparison[]>;
  getContactPortfolios: (query?: string, sort?: SortingField[], shouldFetchNext?: boolean) => Promise<Portfolio[]>;
  createComparison: (contact?: GenericContact) => Promise<Comparison>;
  getComparisonById: (id: string, contact?: ComparisonContact) => Promise<Comparison>;
  loadComparison: (comparison: Comparison) => Promise<Comparison>;
  rerunComparison: (comparison: Comparison) => Promise<Comparison>;
  deleteContactComparison: (comparison: ReducedComparison) => Promise<void>;
  removeMissingComparisonData: (comparison: Comparison) => Promise<Comparison>;
  closeComparison: (comparison: Comparison, resetContact?: boolean) => void;
  getKycQuestionnaires: () => Promise<KycQuestionnaire[]>;
  loadKycQuestionnaire: (questionnaire: KycQuestionnaire) => void;
  resetRiskProfile: () => void;
  saveContactRiskProfile: (profile: Partial<ContactRiskProfile>) => Promise<ContactRiskProfile[]>;
  deleteContactRiskProfile: (profile: ContactRiskProfile) => Promise<void>;
  attachRiskProfile: (profile: RiskProfileItem) => Promise<void>;
  setRiskProfileForEdit: (profile: ContactRiskProfile) => void;
  getContactActivity: (contact: GenericContact, nextToken?: string) => Promise<Activity[]>;
  skipEmptyState: () => void;
  resetGif: () => void;
  setBaseDob: (contact: ComparisonContact) => void;
  setComparisonTab: (path?: string) => void;
  startLoaderAction: (name: string, params: Dictionary<unknown>) => void;
  stopLoaderAction: (name: string) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;
};

type Props = RouteComponentProps<MatchParams> &
  StateProps &
  DispatchProps & {
    user: User;
  };

type State = {
  redirect?: string;
  searchParams?: string;
  selectedComparisonForDelete?: ReducedComparison;
  selectedRiskProfileForDelete?: ContactRiskProfile;
  showDeleteContactModal: boolean;
  showDeleteComparisonModal: boolean;
  showComparisonMissingItemsModal: boolean;
  showRiskProfileModal: boolean;
  showDeleteRiskProfileModal: boolean;
  pendingComparison: boolean;
  attachingRiskProfile: boolean;
  selectedRiskProfileForComparison?: ContactRiskProfile;
};

class ContactDetails extends React.Component<Props, State> {
  readonly state: State = {
    showDeleteContactModal: false,
    showDeleteComparisonModal: false,
    showRiskProfileModal: false,
    showDeleteRiskProfileModal: false,
    showComparisonMissingItemsModal: false,
    pendingComparison: false,
    attachingRiskProfile: false,
  };

  constructor(props: Props) {
    super(props);

    this.loadContactFromParams();
    this.loadQuestionnaires();
  }

  componentWillUnmount() {
    // Reset current contact in Redux when navigation is NOT performed to a comparison page path
    if (this.props.history.location.pathname.indexOf(NAV_PATHS.COMPARISON) === -1) {
      this.props.resetCurrentContact();
    }
  }

  loadContactFromParams = () => {
    const { user } = this.props;
    const { contactId } = this.props.match.params;

    // we want to make sure to set the Persistence Key in redux if the contact id has one.
    if (user != null && contactId != null) {
      const userPersistenceKeys = user?.appConfig.persistenceKeys;
      const persistenceSource = localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE);
      if (userPersistenceKeys?.includes(PERSISTENCE_KEYS.EXISTING_CLIENT_CONTACT) && persistenceSource == null) {
        const regex = new RegExp('^[A-Za-z0-9+/]+={0,2}$');
        if (regex.test(contactId)) {
          const decoded = atob(contactId);
          const testString = `${PERSISTENCE_KEYS.EXISTING_CLIENT_CONTACT}-`;
          if (decoded.startsWith(testString)) {
            this.props.setPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE, PERSISTENCE_KEYS.EXISTING_CLIENT_CONTACT);
          }
        }
      }
    }

    if (contactId != null && user.appConfig.mode === AppMode.CONTACT && userHasPermission(user, PERMISSIONS.PERSIST_CONTACT_ID_IN_SESSION)) {
      sessionStorage.setItem(SessionStorageKey.CONTACT_ID, contactId);
    }

    this.props
      .getContactById(contactId)
      .then(contact => {
        this.props.loadContactData(contact);
      })
      .catch(err => {
        if (err.status === 404) {
          this.setState({ redirect: NAV_PATHS.CONTACTS });
        } else {
          showError(err);
        }
      });
  };

  loadQuestionnaires = () => {
    this.props.getKycQuestionnaires();
  };

  renderLoading = () => <LoadingSpinner />;

  getBreadcrumbs = (): BreadcrumbItem[] => {
    const { user, currentContact } = this.props;

    if (!currentContact) {
      return [];
    }

    const {
      contact: { fname, lname },
    } = currentContact;

    const breadcrumbItems = [];
    if (user.appConfig.mode !== AppMode.CONTACT) {
      breadcrumbItems.push({
        value: formatMessage(`Contacts.ContactTabs.Breadcrumb.${getContactLayout(user) === CONTACT_LAYOUT.CLIENT ? 'Clients' : 'Contacts'}`),
        redirect: NAV_PATHS.CONTACTS,
      });
    }
    breadcrumbItems.push({
      value: (
        <span data-private data-dd-privacy="mask">
          {fname} {lname}
        </span>
      ),
    });
    return breadcrumbItems;
  };

  onSaveContact = (contact: Partial<GenericContact>) => this.props.saveContact(contact);

  onDeleteContact = () => {
    this.setState({ showDeleteContactModal: true });
  };

  onCancelDeleteContact = () => {
    this.setState({ showDeleteContactModal: false });
  };

  onConfirmDeleteContact = () => {
    const { currentContact } = this.props;

    if (!currentContact) {
      return;
    }

    this.props.deleteContact(currentContact.contact).then(() => {
      this.props.resetCurrentContact();
      this.setState({ redirect: NAV_PATHS.CONTACTS });
      notification.success(formatMessage('Contacts.DeleteModal.DeleteOneSuccess'));
    });
  };

  onLoadMorePortfolios = (query?: string) => this.props.getContactPortfolios(query || undefined);

  onLoadMoreComparisons = (query?: string) => {
    const { currentContact } = this.props;

    if (!currentContact) {
      return Promise.reject();
    }

    return this.props.getContactComparisons(currentContact.contact, query || undefined);
  };

  onLoadMoreActivityLogs = () => {
    const { currentContact } = this.props;

    if (!currentContact) {
      return Promise.reject();
    }

    return this.props.getContactActivity(currentContact.contact, currentContact.nextActivityLogToken);
  };

  onCancelDeleteComparison = () => {
    this.setState({
      selectedComparisonForDelete: undefined,
      showDeleteComparisonModal: false,
    });
  };

  onConfirmDeleteComparison = () => {
    const { selectedComparisonForDelete } = this.state;

    if (!selectedComparisonForDelete) {
      return Promise.reject();
    }

    return this.props.deleteContactComparison(selectedComparisonForDelete).then(() => {
      this.onCancelDeleteComparison();
    });
  };

  onSearchComparisons = (query: string) => {
    const { currentContact } = this.props;

    if (!currentContact) {
      return Promise.reject();
    }

    return this.props.getContactComparisons(currentContact.contact, query);
  };

  onCreateComparison = async () => {
    const { currentContact, user } = this.props;
    if (!currentContact) {
      return;
    }

    if (userHasPermissionEnabled(user, PERMISSIONS.BMO_GIF)) {
      this.props.resetGif();
      this.props.skipEmptyState();
      await this.props.createComparison(currentContact.contact);
      this.props.setBaseDob(currentContact.contact);

      this.props.setComparisonTab(COMPARISON_NAV_PATHS.GIF);

      this.setState({
        redirect: `${NAV_PATHS.COMPARISON}${COMPARISON_NAV_PATHS.GIF}`,
      });

      return;
    }

    this.props.createComparison(currentContact.contact).then(() => {
      this.setState({ redirect: NAV_PATHS.COMPARISON });
    });
  };

  onDeleteComparison = (comparison: ReducedComparison) => {
    this.setState({
      selectedComparisonForDelete: comparison,
      showDeleteComparisonModal: true,
    });
  };

  onViewComparison = async (comparison: ReducedComparison) => {
    const { currentContact } = this.props;
    if (!comparison._id) {
      return;
    }

    this.props.startLoaderAction('OPEN_COMPARISON', { comparisonId: comparison._id });
    const dbComparison = await this.props.getComparisonById(comparison._id, comparison.contact);
    const newComparison = await this.props.loadComparison(dbComparison);

    if (comparisonHasMissingItems(newComparison)) {
      this.setState({ showComparisonMissingItemsModal: true });
      this.props.stopLoaderAction('OPEN_COMPARISON');
      return;
    }

    const isGIF = comparisonHasGifItems(dbComparison);

    if (isGIF) {
      this.props.skipEmptyState();
      this.props.resetGif();
    }

    const subPath = isGIF ? COMPARISON_NAV_PATHS.GIF : COMPARISON_NAV_PATHS.PERFORMANCE;

    this.props.setComparisonTab(subPath);

    heapUtils.track(HEAP_CUSTOM_EVENTS.LOAD_EXISTING_COMPARISON);

    this.props.stopLoaderAction('OPEN_COMPARISON');
    const pathname = generatePath(NAV_PATHS.COMPARISON_DETAILS, {
      comparisonId: newComparison._id || '',
    });
    const contactQuery = currentContact ? `?contact=${currentContact.contact._id}` : '';
    this.setState({
      redirect: `${pathname}${subPath}`,
      searchParams: contactQuery,
    });
  };

  onRerunComparison = async (comparison: ReducedComparison) => {
    if (!comparison._id) {
      return;
    }

    this.props.startLoaderAction('RERUN_COMPARISON', { comparisonId: comparison._id });
    const dbComparison = await this.props.getComparisonById(comparison._id, comparison.contact);
    const newComparison = await this.props.rerunComparison(dbComparison);

    await this.props.loadComparison(newComparison);

    if (comparisonHasMissingItems(newComparison)) {
      this.setState({ showComparisonMissingItemsModal: true });
      this.props.stopLoaderAction('RERUN_COMPARISON');
      return;
    }

    const isGIF = comparisonHasGifItems(newComparison);

    if (comparisonHasGifItems(newComparison)) {
      this.props.skipEmptyState();
      this.props.resetGif();
    }

    const subPath = isGIF ? COMPARISON_NAV_PATHS.GIF : COMPARISON_NAV_PATHS.PERFORMANCE;

    this.props.setComparisonTab(subPath);

    heapUtils.track(HEAP_CUSTOM_EVENTS.LOAD_EXISTING_COMPARISON);

    this.props.stopLoaderAction('RERUN_COMPARISON');

    this.setState({
      redirect: `${NAV_PATHS.COMPARISON}/${subPath}`,
    });
  };

  onAttachRiskProfile = (riskProfile: ContactRiskProfile) => {
    this.setState({ attachingRiskProfile: true, selectedRiskProfileForComparison: riskProfile });
    this.props
      .attachRiskProfile({
        ...riskProfile.profile,
        savedSections: riskProfile.savedSections,
        questionnaireId: riskProfile.questionnaire,
        externalData: riskProfile.externalData,
      })
      .then(() => {
        this.setState({ attachingRiskProfile: false, redirect: NAV_PATHS.COMPARISON });
      });
  };

  onDeleteRiskProfile = (riskProfile: ContactRiskProfile) => {
    this.setState({
      selectedRiskProfileForDelete: riskProfile,
      showDeleteRiskProfileModal: true,
    });
  };

  onEditRiskProfile = (riskProfile: ContactRiskProfile) => {
    const { questionnaires } = this.props;

    const questionnaire = questionnaires.find(q => q._id === riskProfile.questionnaire);

    if (!questionnaire) {
      notification.errorFromKey('QUESTIONNAIRE_NOT_FOUND');
      return;
    }

    this.props.setRiskProfileForEdit(riskProfile);
    this.onCreateRiskProfile(questionnaire);
  };

  onCreateRiskProfile = (questionnaire?: KycQuestionnaire) => {
    const { questionnaires } = this.props;

    if (questionnaire) {
      this.props.loadKycQuestionnaire(questionnaire);
    } else {
      this.props.loadKycQuestionnaire(getActiveQuestionnaire(questionnaires));
    }

    this.setState({ showRiskProfileModal: true });
  };

  onSaveRiskProfile = () => {
    const { newRiskProfile } = this.props;

    if (newRiskProfile?.profile) {
      this.props
        .saveContactRiskProfile(newRiskProfile)
        .then(() => {
          this.props.resetRiskProfile();

          this.onCloseCreateRiskProfile();
          notification.success(formatMessage('CreateRiskProfile.SaveSuccess'));
        })
        .catch(() => {
          notification.error(formatMessage('CreateRiskProfile.SaveError'));
        });
    }
  };

  onCloseCreateRiskProfile = () => {
    this.props.resetRiskProfile();
    this.setState({ showRiskProfileModal: false });
  };

  onCancelDeleteRiskProfile = () => {
    this.setState({
      showRiskProfileModal: false,
      selectedRiskProfileForDelete: undefined,
    });
  };

  onConfirmDeleteRiskProfile = async () => {
    const { selectedRiskProfileForDelete } = this.state;

    if (!selectedRiskProfileForDelete) {
      return;
    }

    await this.props.deleteContactRiskProfile(selectedRiskProfileForDelete);
    this.onCancelDeleteRiskProfile();

    notification.success(formatMessage('Contacts.ContactTabs.RiskProfiles.DeleteSuccess'));
  };

  removeMissingItemsAndLoadComparison = async () => {
    const originalComparison = this.props.loadedComparison;
    if (!originalComparison) {
      return;
    }

    let newComparison = await this.props.removeMissingComparisonData(originalComparison);
    newComparison = await this.props.rerunComparison(newComparison);

    this.props.closeComparison(originalComparison, false);
    await this.props.loadComparison(newComparison);

    this.setState({ showComparisonMissingItemsModal: false, redirect: NAV_PATHS.COMPARISON });
  };

  render() {
    const {
      user,
      currentContact,
      loading,
      locale,
      questionnaires,
      currentQuestionnaire,
      newRiskProfile,
      isDeletingContactComparison,
      openingComparisonIds,
      rerunningComparisonIds,
      loadedComparison,
      location,
    } = this.props;

    const {
      redirect,
      searchParams,
      showDeleteContactModal,
      showRiskProfileModal,
      showDeleteComparisonModal,
      selectedComparisonForDelete,
      selectedRiskProfileForDelete,
      showDeleteRiskProfileModal,
      showComparisonMissingItemsModal,
    } = this.state;

    if (redirect) {
      return (
        <Redirect
          push
          to={{
            pathname: redirect,
            search: searchParams ?? undefined,
            state: { from: location },
          }}
        />
      );
    }

    return (
      <>
        <div className="contact-details">
          <Header />
          <OverlappingBackground />

          <main id="content" className="contact-details-container">
            {currentContact && (
              <>
                <Breadcrumb replace items={this.getBreadcrumbs()} />

                <div className="contact-details-content">
                  <LegacyContactInfoCard
                    user={user}
                    locale={locale}
                    contact={currentContact.contact}
                    onSaveContact={this.onSaveContact}
                    onDeleteContact={this.onDeleteContact}
                  />

                  <ContactDetailsTabs
                    user={user}
                    locale={locale}
                    questionnaires={questionnaires}
                    currentContact={currentContact}
                    onLoadMoreComparisons={this.onLoadMoreComparisons}
                    onLoadMoreActivityLogs={this.onLoadMoreActivityLogs}
                    loading={loading.current}
                    attachingRiskProfile={this.state.attachingRiskProfile}
                    selectedRiskProfileForComparison={this.state.selectedRiskProfileForComparison}
                    onCreateComparison={this.onCreateComparison}
                    onDeleteComparison={this.onDeleteComparison}
                    onViewComparison={this.onViewComparison}
                    onRerunComparison={this.onRerunComparison}
                    onSearchComparisons={this.onSearchComparisons}
                    onAttachRiskProfile={this.onAttachRiskProfile}
                    onDeleteRiskProfile={this.onDeleteRiskProfile}
                    onEditRiskProfile={this.onEditRiskProfile}
                    onCreateRiskProfile={this.onCreateRiskProfile}
                    openingComparisonIds={openingComparisonIds}
                    rerunningComparisonIds={rerunningComparisonIds}
                  />
                </div>
              </>
            )}
          </main>
        </div>

        {showDeleteContactModal && currentContact && (
          <DeleteContactsModal
            show={showDeleteContactModal}
            onCancel={this.onCancelDeleteContact}
            onConfirm={this.onConfirmDeleteContact}
            contacts={[currentContact.contact]}
          />
        )}

        {selectedComparisonForDelete && (
          <DeleteComparisonsModal
            isDeleting={isDeletingContactComparison}
            comparisons={[selectedComparisonForDelete]}
            show={showDeleteComparisonModal}
            onCancel={this.onCancelDeleteComparison}
            onConfirm={this.onConfirmDeleteComparison}
          />
        )}

        {showRiskProfileModal && currentQuestionnaire && newRiskProfile && (
          <CreateKycQuestionnaire
            isModal
            show={showRiskProfileModal}
            current={currentContact}
            onSave={this.onSaveRiskProfile}
            onClose={this.onCloseCreateRiskProfile}
            riskProfiles={currentQuestionnaire.riskProfiles}
            newRiskProfile={newRiskProfile}
            minRiskCalculation={currentQuestionnaire.options.minRiskCalculation}
            scoreCalculation={currentQuestionnaire.options.scoreCalculation}
            isSaving={this.props.isSavingRiskProfile}
          />
        )}

        {selectedRiskProfileForDelete && (
          <DestructiveModal
            open={showDeleteRiskProfileModal}
            onOpenChange={open => !open && this.onCancelDeleteRiskProfile()}
            title={formatMessage('DeleteRiskProfile.Title')}
            content={
              <Stack gap="150">
                {(() => {
                  const isFalsyObject = (obj: object) => Object.values(obj).every(value => !value);
                  const riskProfileLabel = [selectedRiskProfileForDelete.name, selectedRiskProfileForDelete.profile?.title].find(
                    label => label && !isFalsyObject(label),
                  );
                  const formattedLabel = formatLocalizedString(riskProfileLabel, locale);
                  return formattedLabel ? (
                    <>
                      <FormattedMessage id="DeleteRiskProfile.Body" />
                      <ul>
                        <li>{formattedLabel}</li>
                      </ul>
                    </>
                  ) : null;
                })()}
                <FormattedMessage id="DeleteRiskProfile.Confirmation" />
              </Stack>
            }
            confirmButtonText={formatMessage('DeleteRiskProfile.ConfirmButton')}
            cancelButtonText={formatMessage('Common.Cancel')}
            onConfirm={this.onConfirmDeleteRiskProfile}
            onCancel={this.onCancelDeleteRiskProfile}
          />
        )}

        {showComparisonMissingItemsModal && loadedComparison && (
          <ComparisonMissingItemsModal
            show={showComparisonMissingItemsModal}
            onCancel={() => {
              this.setState({ showComparisonMissingItemsModal: false });
              this.props.closeComparison(loadedComparison, false);
            }}
            onConfirm={this.removeMissingItemsAndLoadComparison}
            comparison={loadedComparison}
          />
        )}
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => {
  const openingComparisonParams = selectIsLoadingParamsEvery(state, ['OPEN_COMPARISON']);
  const openingComparisonIds = openingComparisonParams.reduce((acc, param: Dictionary<unknown>) => {
    if (param.comparisonId) {
      return [...acc, param.comparisonId as string];
    }

    return acc;
  }, [] as string[]);
  const rerunningComparisonParams = selectIsLoadingParamsEvery(state, ['RERUN_COMPARISON']);
  const rerunningComparisonIds = rerunningComparisonParams.reduce((acc, param: Dictionary<unknown>) => {
    if (param.comparisonId) {
      return [...acc, param.comparisonId as string];
    }

    return acc;
  }, [] as string[]);

  const loadedComparison = state.comparison.activeComparison;

  return {
    loadedComparison,
    questionnaires: state.kycQuestionnaire.questionnaires,
    newRiskProfile: state.kycQuestionnaire.newRiskProfile,
    currentQuestionnaire: state.kycQuestionnaire.currentQuestionnaire,
    currentContact: state.contacts.current,
    loading: state.contacts.loading,
    locale: state.intl.locale,
    isDeletingContactComparison: selectIsLoadingEvery(state, [DELETE_COMPARISON.type]),
    openingComparisonIds,
    rerunningComparisonIds,
    isSavingRiskProfile: selectIsLoadingSome(state, [SET_IS_SAVING_CONTACT_RISK_PROFILE.type, SET_IS_RESETTING_RISK_PROFILE.type]),
  };
};

export default withRouter(
  connect(mapStateToProps, {
    getContactById,
    loadContactData,
    saveContact,
    deleteContact,
    getContactPortfolios,
    getContactComparisons,
    createComparison,
    getComparisonById,
    loadComparison,
    rerunComparison,
    deleteContactComparison,
    getKycQuestionnaires,
    loadKycQuestionnaire,
    resetRiskProfile,
    saveContactRiskProfile,
    deleteContactRiskProfile,
    attachRiskProfile,
    setRiskProfileForEdit,
    getContactActivity,
    skipEmptyState,
    resetGif,
    setBaseDob,
    resetCurrentContact,
    setComparisonTab,
    startLoaderAction,
    stopLoaderAction,
    removeMissingComparisonData,
    closeComparison,
    setPersistenceSource,
  })(ContactDetails),
);
